import React, { useEffect, useMemo, useRef } from "react";
import { Search, X, ChevronDown, RefreshCw, Loader2 } from "lucide-react";
import FloorRow from "./FloorRow";
import {
  HOUSE_DISCOVERY_FILTERS,
  HOUSE_SORT_OPTIONS,
  getHouseHeat,
} from "../../../utils/bondhouseHelpers";

const FloorIndex = ({
  houses,
  selectedId,
  onSelect,
  searchQuery,
  onSearchChange,
  houseFilter,
  onHouseFilterChange,
  discoveryCounts,
  sortBy,
  onSortChange,
  onRefresh,
  refreshing,
  isLoading,
  totalHouses,
}) => {
  const listRef = useRef(null);
  const selectedRef = useRef(null);

  const maxHeat = useMemo(() => {
    if (!houses.length) return 1;
    return Math.max(1, ...houses.map((h) => getHouseHeat(h)));
  }, [houses]);

  useEffect(() => {
    if (selectedRef.current && listRef.current) {
      const r = selectedRef.current.getBoundingClientRect();
      const c = listRef.current.getBoundingClientRect();
      if (r.top < c.top || r.bottom > c.bottom) {
        selectedRef.current.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }
  }, [selectedId]);

  return (
    <aside className="floor-index" aria-label="House ledger">
      <div className="floor-index-head">
        <div className="floor-index-brand">
          <span className="floor-index-brand-label">Floor ledger</span>
          <span className="floor-index-brand-sub">Scan · select · bet</span>
        </div>
        <div className="floor-index-search">
          <Search size={13} className="floor-index-search-ico" aria-hidden />
          <input
            type="search"
            placeholder="Filter houses…"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="floor-index-search-input"
            aria-label="Search houses"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => onSearchChange("")}
              className="floor-index-search-clear"
              aria-label="Clear search"
            >
              <X size={11} />
            </button>
          )}
        </div>

        <div className="floor-index-chips" role="tablist" aria-label="Filter houses">
          {HOUSE_DISCOVERY_FILTERS.map((f) => {
            const count = discoveryCounts?.[f.id] ?? 0;
            const active = houseFilter === f.id;
            return (
              <button
                type="button"
                key={f.id}
                role="tab"
                aria-selected={active}
                onClick={() => onHouseFilterChange(f.id)}
                className={`floor-index-chip ${active ? "floor-index-chip-active" : ""}`}
                title={f.hint}
              >
                <span>{f.label}</span>
                <span className="floor-index-chip-count">{count}</span>
              </button>
            );
          })}
        </div>

        <div className="floor-index-toolbar">
          <label className="floor-index-sort">
            <span className="floor-index-sort-label">Sort</span>
            <span className="floor-index-sort-wrap">
              <select
                value={sortBy}
                onChange={(e) => onSortChange(e.target.value)}
                className="floor-index-sort-select"
                aria-label="Sort houses"
              >
                {HOUSE_SORT_OPTIONS.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </select>
              <ChevronDown size={11} className="floor-index-sort-chev" aria-hidden />
            </span>
          </label>
          <button
            type="button"
            onClick={onRefresh}
            disabled={isLoading || refreshing}
            className="floor-index-refresh"
            aria-label="Refresh houses"
            title="Refresh"
          >
            {refreshing ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <RefreshCw size={12} />
            )}
          </button>
        </div>
      </div>

      <div className="floor-index-summary" aria-live="polite">
        <span>
          <strong>{houses.length}</strong>
          {houses.length === totalHouses
            ? ` listing${houses.length === 1 ? "" : "s"}`
            : ` / ${totalHouses}`}
        </span>
      </div>

      <div className="floor-index-list" ref={listRef}>
        {isLoading && houses.length === 0 ? (
          <div className="floor-index-skel">
            {[0, 1, 2, 3, 4].map((i) => (
              <div className="floor-index-skel-row" key={i} aria-hidden />
            ))}
          </div>
        ) : houses.length === 0 ? (
          <div className="floor-index-empty">
            <p className="floor-index-empty-title">
              {totalHouses === 0 ? "Floor is empty" : "No matches"}
            </p>
            <p className="floor-index-empty-body">
              {totalHouses === 0
                ? "No listings yet."
                : "Clear filters or search."}
            </p>
            {(searchQuery || houseFilter !== "all") && (
              <button
                type="button"
                onClick={() => {
                  onSearchChange("");
                  onHouseFilterChange("all");
                }}
                className="floor-index-empty-reset"
              >
                Reset
              </button>
            )}
          </div>
        ) : (
          houses.map((house, idx) => {
            const heatPct = Math.round((getHouseHeat(house) / maxHeat) * 100);
            return (
              <div
                key={house._id}
                ref={house._id === selectedId ? selectedRef : null}
              >
                <FloorRow
                  house={house}
                  rank={idx + 1}
                  heatPct={heatPct}
                  selected={house._id === selectedId}
                  onSelect={() => onSelect(house._id)}
                />
              </div>
            );
          })
        )}
      </div>
    </aside>
  );
};

export default FloorIndex;
